import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';

// Toast component
const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000); // Auto close after 5 seconds

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 inset-x-4 sm:top-20 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50">
      <div className="bg-green-600 text-white px-3 py-2.5 rounded-md shadow-lg flex items-center justify-between max-w-xs sm:max-w-md mx-auto">
        <div className="flex items-center truncate">
          <svg className="w-5 h-5 flex-shrink-0 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm sm:text-base font-medium truncate">{message}</span>
        </div>
        <button 
          onClick={onClose} 
          className="ml-2 text-white hover:text-green-200 flex-shrink-0 p-1"
          aria-label="Close notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const Feedback = () => {
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedbackError('');
    setEmailError('');
    
    let hasError = false;

    // Validate feedback
    if (!feedback.trim()) {
      setFeedbackError('Feedback is required');
      hasError = true;
    }

    // Validate email if provided
    if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    }

    if (hasError) return;

    try {
      setIsSubmitting(true);
      
      const payload = {
        feedback: feedback,
        email_id: email || undefined
      };
      
      const response = await fetch('https://quizcracker-backend.vercel.app/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }
      
      // Show toast notification instead of inline success message
      setShowToast(true);
      
      // Clear form
      setFeedback('');
      setEmail('');
      
    } catch (err) {
      console.error('Feedback submission error:', err);
      setFeedbackError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="border-b">
        <Header />
      </div>

      {/* Toast notification */}
      {showToast && (
        <Toast 
          message="Thank you for your feedback!" 
          onClose={() => setShowToast(false)} 
        />
      )}

      <div className="flex-1 bg-gray-50 pt-8">
        <div className="max-w-3xl mx-auto py-12 px-4 sm:px-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 sm:p-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3">How are we doing?</h2>
              <p className="text-lg text-gray-600 mb-8">
                We're constantly working to make QuizCult the best quiz app possible. 
                Your feedback is invaluable in helping us achieve that. Please share 
                your thoughts and suggestions below.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <label htmlFor="feedback" className="block text-lg font-medium text-gray-700 mb-2">
                    Your Feedback <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="feedback"
                    rows={7}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Tell us what you think about QuizCult..."
                    className={`w-full px-5 py-4 text-lg border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none ${
                      feedbackError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {feedbackError && (
                    <p className="mt-2 text-base text-red-600">{feedbackError}</p>
                  )}
                  {!feedbackError && (
                    <p className="mt-2 text-sm text-gray-500">Required field</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-lg font-medium text-gray-700 mb-2">
                    Email Address <span className="text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className={`w-full px-5 py-4 text-lg border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      emailError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {emailError ? (
                    <p className="mt-2 text-base text-red-600">{emailError}</p>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">We'll never share your email with anyone else</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 text-white py-4 px-6 rounded-lg hover:bg-indigo-700 
                           transition-all duration-200 text-lg font-medium flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    'Submit Feedback'
                  )}
                </button>
              </form>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <a href="/" className="text-lg text-indigo-600 hover:text-indigo-800 font-medium">
              Return to Home
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Feedback; 